import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { buildOrderUrl } from '../lib/platformLinks';
import {
  STATUS,
  SOURCE_LABEL,
  REPEAT_VERDICT,
  LINE_ITEM_STATUS_ORDER,
  HEADER_MANUAL_STATUS_ORDER,
  lineStatusOptions,
} from '../constants';
import { saveOrder } from '../services/ordersService';

const toDateInput = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromDateInput = (s) => (s ? new Date(`${s}T00:00:00`) : null);

// TextField that shows an orange border + "Was: <original>" tooltip when the
// field has been changed from a (non-blank) imported value.
function OvField({ original, sx, ...props }) {
  const marked = original != null && original !== '';
  const field = (
    <TextField
      {...props}
      sx={{
        ...sx,
        ...(marked
          ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 } }
          : {}),
      }}
    />
  );
  return marked ? (
    <Tooltip title={`Was: ${original}`} arrow placement="top">
      {field}
    </Tooltip>
  ) : (
    field
  );
}

const valueAtPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

// Project line items down to only the editable fields, so comparisons aren't
// thrown off by derived/raw fields (productRaw, isLeather, sortOrder, lineId…).
const projectItems = (items) =>
  (items || []).map((it) => ({
    product: String(it.product || '').trim(),
    variation: String(it.variation || '').trim(),
    buyerNotes: String(it.buyerNotes || '').trim(),
    quantity: Number(it.quantity) || 1,
    unitPrice: Number(it.unitPrice) || 0,
  }));

// Compare original order vs edited form; return the set of changed platform paths.
function changedPaths(original, edited) {
  const paths = [];
  const cmp = (path, a, b) => {
    if (a !== b) paths.push(path);
  };
  cmp('buyer.name', original.buyer?.name || '', edited.buyer.name);
  cmp('buyer.email', original.buyer?.email || '', edited.buyer.email);
  ['name', 'phone', 'address1', 'address2', 'townCity', 'county', 'postcode', 'country', 'service'].forEach((k) =>
    cmp(`shipping.${k}`, original.shipping?.[k] || '', edited.shipping[k])
  );
  cmp('dates.postByTarget', toDateInput(original.dates?.postByTarget), edited.postByTarget || '');
  cmp('money.postage', Number(original.money?.postage) || 0, Number(edited.postage) || 0);
  cmp('money.discount', Number(original.money?.discount) || 0, Number(edited.discount) || 0);
  cmp('money.refund', Number(original.money?.refund) || 0, Number(edited.refund) || 0);
  if (JSON.stringify(projectItems(original.lineItems)) !== JSON.stringify(projectItems(edited.lineItems))) {
    paths.push('lineItems');
  }
  return paths;
}

// Build the overrides map { path: originalValue }, preserving the first-seen
// original (the imported value) across repeated edits.
function buildOverrides(original, edited) {
  const prev = original.overrides || {};
  const out = { ...prev };
  changedPaths(original, edited).forEach((path) => {
    if (!(path in out)) {
      const v = path === 'lineItems' ? 'original line items' : valueAtPath(original, path);
      out[path] = v instanceof Date ? v.toLocaleDateString('en-GB') : v ?? '';
    }
  });
  return out;
}

function initialForm(order) {
  const s = order.shipping || {};
  return {
    buyer: { name: order.buyer?.name || '', email: order.buyer?.email || '' },
    shipping: {
      name: s.name || '',
      phone: s.phone || '',
      address1: s.address1 || '',
      address2: s.address2 || '',
      townCity: s.townCity || '',
      county: s.county || '',
      postcode: s.postcode || '',
      country: s.country || '',
      service: s.service || '',
    },
    postByTarget: toDateInput(order.dates?.postByTarget),
    postage: order.money?.postage ?? '',
    discount: order.money?.discount ?? '',
    refund: order.money?.refund ?? '',
    manualStatus: order.workflow?.manualStatus || '', // '' = Auto (derived)
    posted: !!order.workflow?.posted,
    trackingNumber: order.workflow?.trackingNumber || '',
    egNotes: order.workflow?.egNotes || '',
    ftoc: !!order.workflow?.ftoc,
    repeatConfirmed: order.repeatCustomer?.confirmedByUser ?? null,
    lineItems: (order.lineItems || []).map((li) => ({
      lineId: li.lineId,
      product: li.product || '',
      productRaw: li.productRaw || '',
      variation: li.variation || '',
      buyerNotes: li.buyerNotes || '',
      quantity: li.quantity ?? 1,
      unitPrice: li.unitPrice ?? '',
      sku: li.sku || '',
      isEmbroidery: !!li.isEmbroidery,
      status: li.status || (li.isEmbroidery ? STATUS.PENDING : STATUS.UNCUT),
    })),
  };
}

/** Right-side drawer to view/edit a full order; records overridden fields on save. */
export default function OrderDetailDrawer({ order, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && order) setForm(initialForm(order));
  }, [open, order]);

  if (!order || !form) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }

  const setShip = (k) => (e) => setForm((f) => ({ ...f, shipping: { ...f.shipping, [k]: e.target.value } }));
  const setBuyer = (k) => (e) => setForm((f) => ({ ...f, buyer: { ...f.buyer, [k]: e.target.value } }));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) =>
    setForm((f) => ({ ...f, lineItems: f.lineItems.map((it, n) => (n === i ? { ...it, [k]: e.target.value } : it)) }));
  const addItem = () =>
    setForm((f) => ({
      ...f,
      lineItems: [...f.lineItems, { lineId: `li_${f.lineItems.length + 1}`, product: '', variation: '', buyerNotes: '', quantity: 1, unitPrice: '', isEmbroidery: false, status: STATUS.UNCUT }],
    }));
  const removeItem = (i) => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, n) => n !== i) }));
  // Bulk set only applies to lines for which the status is valid (e.g. a felt
  // status leaves embroidery lines untouched).
  const setAllItemStatuses = (status) =>
    setForm((f) => ({
      ...f,
      lineItems: f.lineItems.map((it) => (lineStatusOptions(it).includes(status) ? { ...it, status } : it)),
    }));

  async function save() {
    setSaving(true);
    try {
      const edited = {
        ...order,
        buyer: { ...order.buyer, name: form.buyer.name.trim(), email: form.buyer.email.trim() },
        shipping: {
          ...order.shipping,
          ...Object.fromEntries(Object.entries(form.shipping).map(([k, v]) => [k, v.trim()])),
        },
        dates: { ...order.dates, postByTarget: fromDateInput(form.postByTarget) },
        money: {
          ...order.money,
          postage: Number(form.postage) || 0,
          discount: Number(form.discount) || 0,
          refund: Number(form.refund) || 0,
        },
        workflow: {
          ...order.workflow,
          manualStatus: form.manualStatus || null, // effective status recomputed by finalizeOrder
          posted: form.posted,
          trackingNumber: form.trackingNumber.trim(),
          egNotes: form.egNotes.trim(),
          ftoc: form.ftoc,
        },
        repeatCustomer: { ...order.repeatCustomer, confirmedByUser: form.repeatConfirmed },
        lineItems: form.lineItems.map((it, n) => ({
          lineId: it.lineId || `li_${n + 1}`,
          product: it.product.trim(),
          productRaw: it.productRaw || it.product.trim(), // preserve raw CSV title
          variation: it.variation.trim(),
          buyerNotes: it.buyerNotes.trim(),
          quantity: Number(it.quantity) || 1,
          unitPrice: Number(it.unitPrice) || 0,
          sku: it.sku || '',
          isLeather: it.product.toLowerCase().includes('leather'),
          status: it.status || STATUS.UNCUT,
          sortOrder: n,
        })),
      };
      const overrides = buildOverrides(order, form);
      await saveOrder(edited, overrides);
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      console.error('Save order failed', e);
      window.alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  const isMaybe = order.repeatCustomer?.verdict === REPEAT_VERDICT.MAYBE;
  const ov = (path) => order.overrides?.[path] ?? null; // original value if changed
  const lineItemsChanged = !!order.overrides && 'lineItems' in order.overrides;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 540 } } }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {SOURCE_LABEL[order.source] || order.source} order {order.platformOrderId}
          </Typography>
          {buildOrderUrl(order) && (
            <Tooltip title="Open on the platform" arrow>
              <IconButton size="small" onClick={() => window.open(buildOrderUrl(order), '_blank', 'noopener')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Ordered {order.dates?.ordered instanceof Date ? order.dates.ordered.toLocaleDateString('en-GB') : '—'}
          {order.platformOrderId2 ? ` • ID2 ${order.platformOrderId2}` : ''}
        </Typography>

        {isMaybe && (
          <Box sx={{ mt: 1.5, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Possible repeat customer ({(order.repeatCustomer.basis || []).join(', ') || 'name/address match'}) — confirm?
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant={form.repeatConfirmed === true ? 'contained' : 'outlined'} onClick={() => setForm((f) => ({ ...f, repeatConfirmed: true }))}>
                Yes, repeat
              </Button>
              <Button size="small" variant={form.repeatConfirmed === false ? 'contained' : 'outlined'} onClick={() => setForm((f) => ({ ...f, repeatConfirmed: false }))}>
                No, new
              </Button>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            select
            label="Order status"
            size="small"
            value={form.manualStatus || '__auto__'}
            onChange={(e) => setForm((f) => ({ ...f, manualStatus: e.target.value === '__auto__' ? '' : e.target.value }))}
            sx={{ minWidth: 180 }}
            helperText={form.manualStatus ? 'Manual override' : `Auto · ${order.workflow?.productionStatus || ''}`}
          >
            <MenuItem value="__auto__">Auto (derived from line items)</MenuItem>
            {HEADER_MANUAL_STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <OvField original={ov('dates.postByTarget')} label="Post by" type="date" size="small" value={form.postByTarget} onChange={set('postByTarget')} InputLabelProps={{ shrink: true }} />
          <FormControlLabel control={<Checkbox checked={form.posted} onChange={(e) => setForm((f) => ({ ...f, posted: e.target.checked }))} />} label="Posted" />
        </Stack>

        <Typography variant="subtitle2" gutterBottom>Buyer</Typography>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <OvField original={ov('buyer.name')} label="Name" size="small" value={form.buyer.name} onChange={setBuyer('name')} />
          <OvField original={ov('buyer.email')} label="Email" size="small" value={form.buyer.email} onChange={setBuyer('email')} />
        </Stack>

        <Typography variant="subtitle2" gutterBottom>Shipping</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <OvField original={ov('shipping.name')} label="Recipient" size="small" value={form.shipping.name} onChange={setShip('name')} />
          <OvField original={ov('shipping.phone')} label="Phone" size="small" value={form.shipping.phone} onChange={setShip('phone')} />
          <OvField original={ov('shipping.address1')} label="Address 1" size="small" value={form.shipping.address1} onChange={setShip('address1')} />
          <OvField original={ov('shipping.address2')} label="Address 2" size="small" value={form.shipping.address2} onChange={setShip('address2')} />
          <OvField original={ov('shipping.townCity')} label="Town/City" size="small" value={form.shipping.townCity} onChange={setShip('townCity')} />
          <OvField original={ov('shipping.county')} label="County" size="small" value={form.shipping.county} onChange={setShip('county')} />
          <OvField original={ov('shipping.postcode')} label="Postcode" size="small" value={form.shipping.postcode} onChange={setShip('postcode')} />
          <OvField original={ov('shipping.country')} label="Country" size="small" value={form.shipping.country} onChange={setShip('country')} />
          <OvField original={ov('shipping.service')} label="Service" size="small" value={form.shipping.service} onChange={setShip('service')} />
        </Box>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Line items
            {lineItemsChanged && (
              <Tooltip title="Line items were edited — protected from re-import" arrow>
                <Chip size="small" color="warning" variant="outlined" label="edited" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </Typography>
          {form.lineItems.length > 1 && (
            <TextField
              select
              size="small"
              label="Set all to…"
              value=""
              onChange={(e) => setAllItemStatuses(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {LINE_ITEM_STATUS_ORDER.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {form.lineItems.map((it, i) => (
          <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField label="Product" size="small" fullWidth value={it.product} onChange={setItem(i, 'product')} />
              <TextField
                select
                label={it.isEmbroidery ? 'Status (embroidery)' : 'Status'}
                size="small"
                value={lineStatusOptions(it).includes(it.status) ? it.status : lineStatusOptions(it)[0]}
                onChange={setItem(i, 'status')}
                sx={{ minWidth: 150 }}
              >
                {lineStatusOptions(it).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
              <IconButton size="small" onClick={() => removeItem(i)} disabled={form.lineItems.length === 1}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField label="Variation" size="small" fullWidth multiline minRows={3} value={it.variation} onChange={setItem(i, 'variation')} sx={{ mb: 1 }} />
            <TextField label="Buyer notes" size="small" fullWidth multiline minRows={2} value={it.buyerNotes} onChange={setItem(i, 'buyerNotes')} sx={{ mb: 1 }} />
            <Stack direction="row" spacing={1}>
              <TextField label="Qty" type="number" size="small" value={it.quantity} onChange={setItem(i, 'quantity')} />
              <TextField label="Unit price" type="number" size="small" value={it.unitPrice} onChange={setItem(i, 'unitPrice')} />
            </Stack>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ mb: 2 }}>
          Add line item
        </Button>

        <Typography variant="subtitle2" gutterBottom>Money</Typography>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <OvField original={ov('money.postage')} label="Postage" type="number" size="small" value={form.postage} onChange={set('postage')} />
          <OvField original={ov('money.discount')} label="Discount" type="number" size="small" value={form.discount} onChange={set('discount')} />
          <OvField original={ov('money.refund')} label="Refund" type="number" size="small" value={form.refund} onChange={set('refund')} />
        </Stack>

        <Typography variant="subtitle2" gutterBottom>Workshop</Typography>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <TextField label="Tracking number" size="small" value={form.trackingNumber} onChange={set('trackingNumber')} />
          <TextField label="EG notes" size="small" multiline minRows={2} value={form.egNotes} onChange={set('egNotes')} />
          <FormControlLabel
            control={<Checkbox checked={form.ftoc} onChange={(e) => setForm((f) => ({ ...f, ftoc: e.target.checked }))} />}
            label={
              <Tooltip title="'Further to our conversation' — changes the letter opener" arrow>
                <span>FTOC (conversation)</span>
              </Tooltip>
            }
          />
        </Stack>
      </Box>

      <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', p: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Drawer>
  );
}
